import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadProductTypeByIdUrl,
  zadProductTypesUrl,
  zadProductTypeStatusUrl,
  zadProductTypeUpdateUrl,
} from '../../api'
import {buildZadListQuery} from '../zad/query'
import {parseZadApiError} from '../zad/errors'
import {toZadProductTypePayload} from '../zad/categoryHelpers'
import {
  CreateZadProductTypeRequest,
  FetchZadProductTypeByIdRequest,
  FetchZadProductTypesRequest,
  UpdateZadProductTypeRequest,
  UpdateZadProductTypeStatusRequest,
} from './types'

export const fetchZadProductTypes = createAsyncThunk(
  'zadProductTypes/fetch',
  async (payload: FetchZadProductTypesRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadProductTypesUrl}?${buildZadListQuery(payload)}`)
      const {productTypes, total, totalPages} = res.data || {}
      return {
        success: true as const,
        productTypes: Array.isArray(productTypes) ? productTypes : [],
        total: typeof total === 'number' ? total : 0,
        totalPages: typeof totalPages === 'number' ? totalPages : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadProductTypeOptions = createAsyncThunk(
  'zadProductTypes/fetchOptions',
  async (
    payload: {category?: string; isActive?: boolean | '' | 'true' | 'false'} | void,
    {rejectWithValue}
  ) => {
    try {
      const filters = payload || {}
      const res = await axios.get(
        `${zadProductTypesUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'name',
          sortOrder: 'asc',
          category: filters.category,
          isActive: filters.isActive,
        })}`
      )
      const {productTypes} = res.data || {}
      return {
        success: true as const,
        productTypes: Array.isArray(productTypes) ? productTypes : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadProductTypeById = createAsyncThunk(
  'zadProductTypes/fetchById',
  async (payload: FetchZadProductTypeByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadProductTypeByIdUrl(payload.id))
      return {
        success: true as const,
        productType: res.data.productType,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadProductType = createAsyncThunk(
  'zadProductTypes/create',
  async (payload: CreateZadProductTypeRequest, {rejectWithValue}) => {
    try {
      const body = toZadProductTypePayload(payload)
      const res = await axios.post(zadProductTypesUrl, body)
      return {
        success: true as const,
        productType: res.data.productType,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadProductType = createAsyncThunk(
  'zadProductTypes/update',
  async (payload: UpdateZadProductTypeRequest, {rejectWithValue}) => {
    try {
      const {id, ...values} = payload
      const body = toZadProductTypePayload(values)
      const res = await axios.post(zadProductTypeUpdateUrl(id), body)
      return {
        success: true as const,
        productType: res.data.productType,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadProductTypeStatus = createAsyncThunk(
  'zadProductTypes/updateStatus',
  async (payload: UpdateZadProductTypeStatusRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadProductTypeStatusUrl(payload.id), {
        isActive: payload.isActive,
      })
      return {
        success: true as const,
        productType: res.data.productType,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
